/**
 * useReceiptUploadSocketSync: keeps the signed-in vendor's receipt uploads,
 * and what a finished one changed, current on every page. Mounted in the
 * header beside the unread-count sync, so a push reaches the cache wherever
 * the vendor is, and the receipt pages trust what they hold instead of
 * reading again on every visit (docs/plans/receipts-redesign.md, Requests
 * the receipt pages send 3, 6 and 8).
 *
 * The read job pushes the upload as it stands after each file
 * (RECEIPT_SOCKET_EVENTS.UPLOAD_CHANGED), never on a timer (Tyler,
 * 2026-10-13: "We don't poll here thats why we use sockets"):
 * - the banners' list writes the push into itself, in its own
 *   onCacheEntryAdded (receiptsApi, getReceiptUploads), as RTK Query's
 *   streaming updates do, so the count read moves with no request;
 * - here, a finished upload settled the batch on the server, so everything
 *   the settle changes is marked stale (refreshAfterUploadRead), wherever
 *   the vendor is.
 *
 * Support reading one of the store's receipts again pushes
 * RECEIPT_SOCKET_EVENTS.RECEIPTS_CHANGED, which marks the receipts, the
 * batch, the receipts ready and the tax paid stale: the pages keep what they
 * read until something says it changed (receiptsApi).
 *
 * A socket that dropped may have missed a push, so on Socket.IO's own
 * "connect", which fires on every reconnect, the list and everything a
 * finish changes are marked stale. A push that fails its schema is logged
 * here and never half applied: it may have been a finish, so what a finish
 * changes is marked stale, and the list reads itself again. The socket comes
 * from context, as the header's own listeners take it; the effect runs again
 * when it appears.
 */
import { useEffect } from 'react'
import { RECEIPT_SOCKET_EVENTS, ReceiptUploadChangedSchema } from '@gearghost/api-schemas/receipts'
import { receiptsApi, refreshAfterUploadRead } from '@/redux/api/receiptsApi'
import { useAppDispatch } from '@/redux/hooks'
import { SOCKET_CONNECT, useSocket } from '@/redux/socketContext'
import { createLogger } from '@/utils/logger'

const log = createLogger('useReceiptUploadSocketSync')

export const useReceiptUploadSocketSync = () => {
  const { socket } = useSocket()
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (!socket) return
    const changed = (message: { payload: unknown }) => {
      const parsed = ReceiptUploadChangedSchema.safeParse(message.payload)
      if (!parsed.success) {
        log.error({ issues: parsed.error.issues }, 'an upload-changed push did not carry its upload; what a finish changes is read again')
        refreshAfterUploadRead(dispatch)
        return
      }
      if (parsed.data.upload.finished) refreshAfterUploadRead(dispatch)
    }
    const receiptsChanged = () => {
      dispatch(receiptsApi.util.invalidateTags(['Receipts', 'Batch', 'ReadyReceipts', 'TaxPaid']))
    }
    const reconnected = () => {
      dispatch(receiptsApi.util.invalidateTags(['ReceiptUploads']))
      refreshAfterUploadRead(dispatch)
    }
    socket.on(RECEIPT_SOCKET_EVENTS.UPLOAD_CHANGED, changed)
    socket.on(RECEIPT_SOCKET_EVENTS.RECEIPTS_CHANGED, receiptsChanged)
    socket.on(SOCKET_CONNECT, reconnected)
    return () => {
      socket.off(RECEIPT_SOCKET_EVENTS.UPLOAD_CHANGED, changed)
      socket.off(RECEIPT_SOCKET_EVENTS.RECEIPTS_CHANGED, receiptsChanged)
      socket.off(SOCKET_CONNECT, reconnected)
    }
  }, [socket, dispatch])
}
